import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"
import { showConfirmModal, showAlertModal } from "../lib/custom_confirm"
import { syncGateReason } from "../lib/gate_reason"
import { setValueOrEmpty } from "../lib/empty_value"
import { setButtonLoading } from "../lib/button_loading"

// Gates each bucket's Run button (valid positive-integer Days AND the bucket is
// not currently queued/running) and polls the status endpoint so a finished run
// re-enables Run and refreshes counts without a page reload.
//
// A11Y-1 / BUTTON-4 (#13355): every disabled frame of a Run says which gate
// holds it — the reason as the button's own gl-tooltip plus a "<label> —
// <reason>" accessible name (lib/gate_reason) — and both are withdrawn the
// moment a valid N enables it. The gate itself is unchanged.
export default class extends Controller {
  static targets = ["row", "days", "run", "total", "available", "initiatedBy", "startedAt", "finishedAt",
                    "lastNDays", "created", "countsUpdated", "activeDays", "refresh",
                    "markFailedForm"]
  // The two Run reasons are authored ONCE, in admin/bucket_maint_one_off/index,
  // which paints each row's disabled Run with the matching one server-side and
  // stamps both here as values, so the first paint and every later gate change
  // (typing, a poll that starts or finishes a run) say the same thing. The
  // defaults only serve a DOM that stamps nothing (the Jest fixtures).
  static values = {
    statusUrl: String,
    refreshUrl: String,
    pollMs: { type: Number, default: 5000 },
    daysReason: { type: String, default: "Enter a positive number of days first" },
    activeReason: { type: String, default: "A run for this bucket is already in progress" },
    // Manage Aged Inventory (#17009): the refusal sentence when the viewer lacks
    // the right ("" when they hold it). It outranks every other gate, so typing
    // or a poll never enables a Run the server would refuse.
    rightReason: { type: String, default: "" }
  }

  // Holds each form whose modal the user just accepted, right before
  // requestSubmit() re-fires its submit, so the confirm handlers let that
  // re-entrant submit through. Per form: the Run and Mark-as-failed forms of one
  // row must never confirm each other.
  #confirmedForms = new WeakSet()

  // The "Table data updated …" text captured when Refresh was clicked; null when
  // no refresh is pending. The enqueued job's only observable effect is that
  // stamp moving, so poll() re-enables the button on the first change.
  #stampBeforeRefresh = null

  connect() {
    this.validate()
    if (this.hasStatusUrlValue) this._scheduleNextPoll()
  }

  disconnect() {
    this.stopped = true
    if (this.timer) clearTimeout(this.timer)
  }

  // Self-scheduling poll: the next tick is queued only after the current one
  // resolves, so a slow status endpoint can never pile up overlapping requests
  // (which setInterval would do — it fires on a fixed clock regardless).
  _scheduleNextPoll() {
    this.timer = setTimeout(async () => {
      await this.poll()
      if (!this.stopped) this._scheduleNextPoll()
    }, this.pollMsValue)
  }

  // Enable Run only when Days is a positive integer and the row is not active.
  // A disabled Run names its gate: the run in progress wins over the blank N,
  // since Days is locked (and cleared) while the bucket is busy.
  validate() {
    this.daysTargets.forEach((input, i) => {
      const active = input.dataset.active === "true"
      const valid = /^\d+$/.test(input.value.trim()) && parseInt(input.value, 10) > 0
      const denied = this.rightReasonValue !== ""
      input.disabled = active || denied
      syncGateReason(this.runTargets[i], {
        ready: !denied && !active && valid,
        reason: denied ? this.rightReasonValue : (active ? this.activeReasonValue : this.daysReasonValue)
      })
    })
  }

  // Confirm before launching — it copies large numbers of leads and can run for
  // hours. Uses the app's styled modal (gl-modal), never the browser-native
  // window.confirm.
  async confirmRun(event) {
    const form = event.target
    const bucket = form.querySelector("input[name='bucket_key']")?.value
    const days = form.querySelector("input[name='days']")?.value
    await this._confirmSubmit(event,
      `Are you sure you want to run bucket ${bucket} with ${days} day(s)?\nThis can take hours.`)
  }

  // Mark as failed (#13647) frees a bucket whose run's worker died; a live run
  // would stop at its next heartbeat and its partial copy is kept, so make the
  // admin say it is deliberate.
  async confirmMarkFailed(event) {
    await this._confirmSubmit(event,
      "Mark this run as failed?\nOnly do this if its worker is gone: the bucket unlocks and a new run can start." +
      " Leads it already copied stay.")
  }

  // Because the modal is async (Promise) and form submit is synchronous, always
  // prevent the native submit, then re-submit once accepted — remembering the
  // form so the re-entrant submit isn't re-prompted.
  async _confirmSubmit(event, message) {
    const form = event.target
    if (this.#confirmedForms.has(form)) {
      this.#confirmedForms.delete(form)
      return
    }

    event.preventDefault()
    const ok = await showConfirmModal(message)
    // Bail if cancelled, or if the form was detached while the modal was open.
    if (!ok || !form.isConnected) return

    this.#confirmedForms.add(form)
    form.requestSubmit()
  }

  // Queue an on-demand recompute of the cached counts. The COUNTs are far too
  // slow to run in-request, so the server only enqueues the scheduled job; the
  // poller delivers the new numbers when it lands. The button stays busy until
  // then, so a re-click can't be silently dropped by the job's until_executed
  // lock.
  async refreshCounts() {
    this._setRefreshing(true)
    this.#stampBeforeRefresh = this.hasCountsUpdatedTarget ? this.countsUpdatedTarget.textContent : ""
    const response = await post(this.refreshUrlValue)
    if (response.ok) return

    this.#stampBeforeRefresh = null
    this._setRefreshing(false)
    await showAlertModal("Could not queue the counts refresh. Please try again.")
  }

  async poll() {
    const response = await get(this.statusUrlValue)
    if (!response.ok) return
    const { buckets, counts_updated_at: countsUpdatedAt } = await response.json
    buckets.forEach((bucket) => this._applyRow(bucket))
    // Same wording as Admin::BucketMaintOneOffHelper#counts_updated_label.
    if (this.hasCountsUpdatedTarget && countsUpdatedAt) {
      this.countsUpdatedTarget.textContent = `Table data updated ${countsUpdatedAt}`
    }
    this._maybeFinishRefresh()
    this.validate()
  }

  // A pending refresh is "done" when the stamp text moves off its click-time
  // value — the recompute job's only observable effect.
  _maybeFinishRefresh() {
    if (this.#stampBeforeRefresh === null || !this.hasCountsUpdatedTarget) return
    if (this.countsUpdatedTarget.textContent === this.#stampBeforeRefresh) return
    this.#stampBeforeRefresh = null
    this._setRefreshing(false)
  }

  // BUTTON-8: busy keeps the "Refresh" label — spinner in the icon's place,
  // aria-busy, no second click — through the shared helper.
  _setRefreshing(refreshing) {
    if (this.hasRefreshTarget) setButtonLoading(this.refreshTarget, refreshing)
  }

  _applyRow(bucket) {
    const row = this.rowTargets.find((r) => r.dataset.bucketKey === String(bucket.bucket_key))
    if (!row) return
    const set = (name, value) => {
      const el = row.querySelector(`[data-bucket-maint-target='${name}']`)
      if (el) setValueOrEmpty(el, value)
    }
    set("total", bucket.total)
    set("available", bucket.available)
    set("initiatedBy", bucket.initiated_by)
    set("startedAt", bucket.started_at)
    set("finishedAt", bucket.finished_at)
    set("lastNDays", bucket.last_n_days)
    set("created", bucket.leads_created)
    const days = row.querySelector("[data-bucket-maint-target='days']")
    if (days) {
      const wasActive = days.dataset.active === "true"
      days.dataset.active = String(bucket.active)
      // A finished run (active → inactive) clears the chosen N so the next run
      // starts clean and Run stays disabled (validate() runs right after). Only on
      // the transition — never on later idle polls, which would wipe a value the
      // user is mid-typing for the next run.
      if (wasActive && !bucket.active) days.value = ""
    }
    // Keep the read-only in-flight caption in sync: show which N is running and
    // since when while active, clear it the moment the run finishes (mirrors the
    // cleared input). Same wording as Admin::BucketMaintOneOffHelper#active_run_caption.
    const activeDays = row.querySelector("[data-bucket-maint-target='activeDays']")
    if (activeDays) activeDays.textContent = this._activeCaption(bucket)
    // Mark as failed appears once the server says the run is old enough, and
    // follows the current run's URL; hidden again the moment the bucket is idle.
    const markFailed = row.querySelector("[data-bucket-maint-target='markFailedForm']")
    if (markFailed) {
      markFailed.hidden = !bucket.can_mark_failed
      if (bucket.mark_failed_url) markFailed.action = bucket.mark_failed_url
    }
  }

  _activeCaption(bucket) {
    if (!bucket.active || !bucket.active_days) return ""
    const status = bucket.status === "queued" ? "Queued" : "Running"
    const since = bucket.active_age ? ` · ${bucket.active_verb || "started"} ${bucket.active_age} ago` : ""
    return `${status} · N = ${bucket.active_days}${since}`
  }
}
